import { useRef } from 'react';
import InvestingStyleCards from '../components/InvestingStyleCards';
import InvestingStyleInsight from '../components/InvestingStyleInsight';
import {
  ONBOARDING_INSIGHT_SECTION_CLASS,
  ONBOARDING_SCROLL_AREA_CLASS,
  ONBOARDING_STEP_BODY_CLASS,
} from '../constants';
import {
  sumInvestingStyleCounts,
  useOnboardingInvestingStyleCounts,
} from '../hooks/useOnboardingInvestingStyleCounts';
import type { InvestingStyleId } from '../types';

interface InvestingStyleStepProps {
  selected: InvestingStyleId[];
  isActive: boolean;
  onToggle: (id: InvestingStyleId) => void;
}

export default function InvestingStyleStep({
  selected,
  isActive,
  onToggle,
}: InvestingStyleStepProps) {
  // Prefetch all style counts while step 2 is visible (before first selection).
  const countsByStyleId = useOnboardingInvestingStyleCounts(isActive);
  const countSummary = sumInvestingStyleCounts(selected, countsByStyleId);

  // Skip analyzing only when the step opened with selections already present.
  // Clearing all styles resets this so the next pick shows loading again.
  const skipInitialAnimationRef = useRef(selected.length > 0);
  if (selected.length === 0) {
    skipInitialAnimationRef.current = false;
  }

  const contentClass = 'mx-auto w-full px-2 max-w-md sm:max-w-sm';
  const horizontalInsetClass = 'px-1 sm:px-0';

  return (
    <div className={ONBOARDING_STEP_BODY_CLASS}>
      <div className={`${ONBOARDING_SCROLL_AREA_CLASS} ${horizontalInsetClass}`}>
        <div className={`${contentClass} pb-2 sm:pb-3`}>
          <InvestingStyleCards selected={selected} onToggle={onToggle} />
        </div>
      </div>

      {selected.length > 0 ? (
        <div className={`${ONBOARDING_INSIGHT_SECTION_CLASS} ${horizontalInsetClass}`}>
          <InvestingStyleInsight
            styleIds={selected}
            matchCount={countSummary.total}
            isCountLoading={countSummary.isLoading}
            hasCountError={countSummary.error}
            skipInitialAnimation={skipInitialAnimationRef.current}
          />
        </div>
      ) : null}
    </div>
  );
}
